import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import {
  ArrowRight,
  CheckCircle2,
  Mail,
  Megaphone,
  ShieldCheck,
} from 'lucide-react';
import Logo from '../components/common/Logo';
import SocialLinks from '../components/common/SocialLinks';

const SITE_URL = 'https://ballotr.com';
const PAGE_TITLE = 'About Ballotr | Political News Into Civic Action';
const PAGE_DESCRIPTION =
  'Learn how Ballotr turns political news and public issues into campaigns people can support or oppose through visible civic action.';
const PAGE_IMAGE = `${SITE_URL}/logo192.png`;
const ADS_EMAIL = 'ads@ballotr.com';

const howItWorksItems = [
  'Discover campaigns based on real political events and news',
  'Upvote or downvote campaigns to signal public sentiment',
  'Support or oppose campaigns through financial contributions',
  'Direct contributions to political organizations that align with their position',
  'Track which issues are gaining momentum across the community',
];

const whyItMattersItems = [
  'Understand the issues',
  'Express their stance',
  'Mobilize financial and community support',
];

const explainerSteps = [
  {
    title: '1. Discover',
    description:
      'Browse campaigns based on political news, public issues, and real-world initiatives.',
  },
  {
    title: '2. Take a Side',
    description:
      'Upvote, downvote, support, or oppose campaigns based on your stance.',
  },
  {
    title: '3. Drive Action',
    description:
      'Direct contributions and signal momentum around the issues that matter.',
  },
];

const sponsorSteps = [
  {
    title: '1. Reach Out',
    description:
      'Contact Ballotr with your organization, goals, preferred timing, and the campaign, initiative, or issue you want to promote.',
  },
  {
    title: '2. Review & Fit',
    description:
      'We review sponsorship requests for platform fit, transparency, destination quality, and alignment with Ballotr standards.',
  },
  {
    title: '3. Confirm Placement',
    description:
      'If approved, we align on placement details, timing, destination URL, and final sponsorship language before launch.',
  },
  {
    title: '4. Launch Sponsored Campaign',
    description:
      'Your sponsored campaign appears within the Ballotr feed as a clearly labeled placement designed to fit the platform experience while preserving trust.',
  },
];

const sponsorBenefits = [
  'In-feed sponsored campaign placement',
  'Clear disclosure and transparent labeling',
  'Manual review for quality and platform fit',
  'Simple launch process for early sponsors and partners',
];

const whoItsFor = [
  'Brands and companies',
  'Nonprofits and community organizations',
  'Public awareness and issue campaigns',
  'Approved sponsors and strategic partners',
];

const placementDetails = [
  'Sponsored placements appear within the Ballotr feed',
  'Each placement is clearly labeled as sponsored',
  'Sponsors can direct users to an approved destination URL',
  'Placements are designed to feel native to the Ballotr experience without disguising sponsorship',
];

const inquiryItems = [
  'Organization name and website',
  'Campaign, initiative, or issue you want to promote',
  'Preferred timing or launch window',
  'Destination link',
  'Primary goals for the sponsorship',
  'Budget range or expected scope',
];

const AboutPage: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    if (!location.hash) {
      return;
    }

    const section = document.querySelector(location.hash);

    if (section) {
      section.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [location.hash]);

  return (
    <>
      <Helmet>
        <title>{PAGE_TITLE}</title>
        <meta name="description" content={PAGE_DESCRIPTION} />
        <meta name="robots" content="index,follow" />
        <link rel="canonical" href={`${SITE_URL}/about`} />
        <meta property="og:site_name" content="Ballotr" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={PAGE_TITLE} />
        <meta property="og:description" content={PAGE_DESCRIPTION} />
        <meta property="og:url" content={`${SITE_URL}/about`} />
        <meta property="og:image" content={PAGE_IMAGE} />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={PAGE_TITLE} />
        <meta name="twitter:description" content={PAGE_DESCRIPTION} />
        <meta name="twitter:image" content={PAGE_IMAGE} />
      </Helmet>

      <main className="min-h-screen bg-white text-textPrimary px-6 py-10 md:px-20">
        <div className="max-w-5xl mx-auto">
          <section className="text-center mb-14">
            <div className="flex justify-center mb-6">
              <Logo size="medium" />
            </div>

            <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">
              Why Ballotr?
            </h1>

            <p className="text-xl md:text-2xl font-semibold text-textPrimary max-w-3xl mx-auto leading-relaxed mb-5">
              Turn political news into visible civic action.
            </p>

            <p className="text-lg leading-relaxed text-textPrimary max-w-3xl mx-auto mb-8">
              Ballotr helps people respond to real political issues by voting, supporting, or
              opposing campaigns tied to measurable public participation.
            </p>

            <div className="flex justify-center">
              <a
                href="/auth"
                className="bg-ballotr-blue hover:bg-ballotr-blue-light text-white font-semibold px-6 py-3 rounded-md shadow-md transition"
              >
                Get Started — Create Your Account
              </a>
            </div>
          </section>

          <section
           id="official-sources"
           className="scroll-mt-28 mb-14 rounded-2xl border border-blue-200 bg-blue-50 p-6 md:p-8"
         >
           <div className="flex items-start gap-3">
             <ShieldCheck className="mt-1 h-6 w-6 shrink-0 text-ballotr-blue" />

         <div>
           <h2 className="text-2xl font-bold mb-3">
             Independent Platform and Information Sources
           </h2>
       
           <p className="text-lg leading-relaxed mb-4">
             Ballotr is an independent civic engagement platform. It is not
             affiliated with, endorsed by, or operated by any federal, state,
             or local government entity. Ballotr does not provide official
             government services.
           </p>
       
           <p className="text-lg leading-relaxed mb-4">
             Some Ballotr campaigns are based on official government information.
             Ballotr currently uses the Federal Register for executive orders and
             GovInfo for congressional bills. Other campaigns may be based on
             third-party news reporting and link to the associated original source.
           </p>
       
           <div className="flex flex-col items-start gap-2">
             <a
               href="https://www.federalregister.gov/presidential-documents/executive-orders"
               target="_blank"
               rel="noopener noreferrer"
               className="font-semibold text-ballotr-blue hover:underline"
             >
               Federal Register — Executive Orders
             </a>
       
             <a
               href="https://www.govinfo.gov/app/collection/bills/"
               target="_blank"
               rel="noopener noreferrer"
               className="font-semibold text-ballotr-blue hover:underline"
             >
               GovInfo — Congressional Bills
             </a>
           </div>
         </div>
       </div>
       ```
       
         </section>       

          <section className="mb-14">
            <div className="grid gap-6 md:grid-cols-3">
              {explainerSteps.map((step) => (
                <div
                  key={step.title}
                  className="rounded-2xl border border-gray-200 bg-gray-50 p-6 md:p-7 shadow-sm"
                >
                  <h2 className="text-xl font-bold mb-3">{step.title}</h2>
                  <p className="text-base md:text-lg leading-relaxed text-textPrimary">
                    {step.description}
                  </p>
                </div>
              ))}
            </div>
          </section>

          <div className="border-t border-gray-200 my-12" />

          <section className="mb-12">
            <div className="flex items-center mb-6">
              <Logo size="medium" className="mr-4" />
              <h2 className="text-3xl font-bold">Why Ballotr?</h2>
            </div>

            <p className="text-lg leading-relaxed mb-6">
              Ballotr is a civic engagement platform that turns political news and public issues
              into actionable campaigns.
            </p>

            <p className="text-lg leading-relaxed mb-6">
              Instead of simply reading headlines or debating online, Ballotr allows people to{' '}
              <strong>support or oppose</strong> real-world political efforts with measurable
              impact.
            </p>

            <p className="text-lg leading-relaxed mb-8">
              Every campaign represents a political issue, news story, or public initiative. Users
              can respond by voting, discussing, and directing contributions to organizations that
              align with their position.
            </p>

            <div className="rounded-2xl border border-gray-200 bg-gray-50 px-6 py-6 md:px-8">
              <p className="text-xl md:text-2xl font-semibold leading-relaxed">
                Ballotr transforms political opinion into visible civic action.
              </p>
            </div>
          </section>

          <div className="border-t border-gray-200 my-12" />

          <section className="bg-gray-50 rounded-2xl p-8 md:p-10 mb-12">
            <h2 className="text-2xl font-bold mb-6">How Ballotr Works</h2>

            <div className="space-y-4 mb-8">
              {howItWorksItems.map((item) => (
                <div key={item} className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 mt-1 text-ballotr-blue shrink-0" />
                  <p className="text-lg leading-relaxed">{item}</p>
                </div>
              ))}
            </div>

            <p className="text-lg leading-relaxed">
              Instead of endless commentary, Ballotr focuses on{' '}
              <strong>measurable civic participation.</strong>
            </p>
          </section>

          <div className="border-t border-gray-200 my-12" />

          <section className="mb-12">
            <h2 className="text-2xl font-bold mb-4">Why It Matters</h2>

            <p className="text-lg leading-relaxed mb-6">
              Political engagement today often happens in fragmented spaces — news sites, social
              media, fundraising platforms, and activist communities.
            </p>

            <p className="text-lg leading-relaxed mb-6">
              Ballotr brings those elements together into a single place where people can:
            </p>

            <div className="space-y-4 mb-8">
              {whyItMattersItems.map((item) => (
                <div key={item} className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 mt-1 text-ballotr-blue shrink-0" />
                  <p className="text-lg leading-relaxed">{item}</p>
                </div>
              ))}
            </div>

            <p className="text-lg leading-relaxed">
              The goal is not just to inform users, but to help them move from awareness to action.
            </p>
          </section>

          <div className="border-t border-gray-200 my-12" />

          <section className="rounded-2xl border border-gray-200 bg-gray-50 px-6 py-8 md:px-8">
            <h2 className="text-2xl font-bold mb-4">Built for Real Participation</h2>

            <p className="text-lg leading-relaxed mb-6">
              Ballotr is built for people who want to do more than scroll through political news.
            </p>

            <p className="text-lg leading-relaxed mb-6">
              Every campaign starts with a real issue shaping public debate.
            </p>

            <div className="space-y-3 mb-8">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="text-blue-600 w-5 h-5" />
                <p className="text-lg">Read the story</p>
              </div>

              <div className="flex items-center gap-3">
                <CheckCircle2 className="text-blue-600 w-5 h-5" />
                <p className="text-lg">Take a side</p>
              </div>

              <div className="flex items-center gap-3">
                <CheckCircle2 className="text-blue-600 w-5 h-5" />
                <p className="text-lg">Turn your stance into real political action</p>
              </div>
            </div>

            <p className="text-2xl font-semibold text-center">Engage. Decide. Act.</p>
          </section>

          <div className="border-t border-gray-200 my-12" />

          <section id="advertise" className="scroll-mt-28">
            <div className="text-center mb-14">
              <div className="inline-flex items-center gap-2 rounded-full border border-blue-100 bg-blue-50 px-4 py-2 text-sm font-semibold text-ballotr-blue mb-5">
                <Megaphone className="h-4 w-4" />
                Sponsored campaign placements
              </div>

              <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">
                Advertise with Ballotr
              </h2>

              <p className="text-xl md:text-2xl font-semibold text-textPrimary max-w-3xl mx-auto leading-relaxed mb-5">
                Reach civically engaged users through transparent sponsored placement.
              </p>

              <p className="text-lg leading-relaxed text-textPrimary max-w-3xl mx-auto mb-8">
                Ballotr offers clearly labeled sponsored campaign placements for brands,
                nonprofits, public awareness efforts, and approved partners. Sponsorships are
                reviewed for fit and integrated into the Ballotr feed in a way that supports
                visibility while preserving user trust.
              </p>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <a
                  href={`mailto:${ADS_EMAIL}?subject=Ballotr%20Advertising%20Inquiry`}
                  className="inline-flex items-center justify-center gap-2 bg-ballotr-blue hover:bg-ballotr-blue-light text-white font-semibold px-6 py-3 rounded-md shadow-md transition"
                >
                  <Mail className="h-5 w-5" />
                  Contact Advertising
                </a>

                <a
                  href="#advertise-how-it-works"
                  className="inline-flex items-center justify-center gap-2 border border-gray-300 text-textPrimary hover:border-ballotr-blue hover:text-ballotr-blue font-semibold px-6 py-3 rounded-md transition"
                >
                  See How It Works
                  <ArrowRight className="h-4 w-4" />
                </a>
              </div>
            </div>

            <section className="mb-14 rounded-2xl border border-gray-200 bg-white p-6 md:p-8 shadow-sm">
              <h3 className="text-2xl font-bold mb-4">Why advertise with Ballotr</h3>
              <p className="text-lg leading-relaxed text-textPrimary">
                Ballotr is built around public issues, participation, and action. Sponsored
                placements give approved advertisers a way to appear within a civic engagement
                context rather than a generic display ad environment.
              </p>
            </section>

            <section className="mb-14 grid gap-6 md:grid-cols-2">
              <div className="rounded-2xl border border-gray-200 bg-gray-50 p-6 md:p-8 shadow-sm">
                <h3 className="text-2xl font-bold mb-4">Who this is for</h3>
                <div className="space-y-4">
                  {whoItsFor.map((item) => (
                    <div key={item} className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 mt-1 text-ballotr-blue shrink-0" />
                      <p className="text-lg leading-relaxed">{item}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-2xl border border-gray-200 bg-gray-50 p-6 md:p-8 shadow-sm">
                <h3 className="text-2xl font-bold mb-4">What’s included</h3>
                <div className="space-y-4">
                  {sponsorBenefits.map((item) => (
                    <div key={item} className="flex items-start gap-3">
                      <ShieldCheck className="w-5 h-5 mt-1 text-ballotr-blue shrink-0" />
                      <p className="text-lg leading-relaxed">{item}</p>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            <section className="mb-14 rounded-2xl border border-gray-200 bg-gray-50 p-6 md:p-8 shadow-sm">
              <h3 className="text-2xl font-bold mb-4">Placement details</h3>
              <div className="space-y-4">
                {placementDetails.map((item) => (
                  <div key={item} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 mt-1 text-ballotr-blue shrink-0" />
                    <p className="text-lg leading-relaxed">{item}</p>
                  </div>
                ))}
              </div>
            </section>

            <div className="border-t border-gray-200 my-12" />

            <section id="advertise-how-it-works" className="mb-12 scroll-mt-28">
              <h3 className="text-3xl font-bold text-center mb-8">
                How sponsorship works
              </h3>

              <div className="grid gap-6 md:grid-cols-2">
                {sponsorSteps.map((step) => (
                  <div
                    key={step.title}
                    className="rounded-2xl border border-gray-200 bg-white p-6 md:p-7 shadow-sm"
                  >
                    <h4 className="text-xl font-bold mb-3">{step.title}</h4>
                    <p className="text-base md:text-lg leading-relaxed text-textPrimary">
                      {step.description}
                    </p>
                  </div>
                ))}
              </div>
            </section>

            <div className="border-t border-gray-200 my-12" />

            <section className="rounded-2xl border border-blue-100 bg-blue-50 px-6 py-8 md:px-8">
              <h3 className="text-2xl font-bold mb-4">Contact Ballotr advertising</h3>

              <p className="text-lg leading-relaxed mb-5">
                To discuss sponsored placement, send us a short note with the basics of what
                you want to promote and what timing you have in mind.
              </p>

              <div className="rounded-xl border border-white/70 bg-white px-5 py-5 shadow-sm mb-6">
                <p className="text-sm font-semibold uppercase tracking-wide text-gray-500 mb-2">
                  Advertising contact
                </p>
                <a
                  href={`mailto:${ADS_EMAIL}?subject=Ballotr%20Advertising%20Inquiry`}
                  className="text-xl font-semibold text-ballotr-blue hover:underline break-all"
                >
                  {ADS_EMAIL}
                </a>
                <p className="text-sm text-textSecondary mt-3">
                  All sponsorships are subject to Ballotr review for platform fit,
                  transparency, and compliance with our standards.
                </p>
              </div>

              <div className="rounded-xl border border-blue-100 bg-white px-5 py-5 shadow-sm">
                <h4 className="text-lg font-bold mb-3">What to include in your inquiry</h4>
                <div className="space-y-3">
                  {inquiryItems.map((item) => (
                    <div key={item} className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 mt-1 text-ballotr-blue shrink-0" />
                      <p className="text-base md:text-lg leading-relaxed">{item}</p>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            <section className="mt-14 rounded-2xl border border-gray-200 bg-white p-6 md:p-8 shadow-sm">
              <h3 className="text-2xl font-bold mb-6">Advertising FAQ</h3>

              <div className="space-y-6">
                <div>
                  <h4 className="text-lg font-bold mb-2">Are all sponsors accepted?</h4>
                  <p className="text-base md:text-lg leading-relaxed text-textPrimary">
                    No. Ballotr reviews sponsorship requests for fit, transparency, and
                    compliance with platform standards before approving placement.
                  </p>
                </div>

                <div>
                  <h4 className="text-lg font-bold mb-2">
                    Will sponsored content be labeled?
                  </h4>
                  <p className="text-base md:text-lg leading-relaxed text-textPrimary">
                    Yes. Sponsored placements are clearly labeled to preserve trust and
                    transparency within the Ballotr experience.
                  </p>
                </div>

                <div>
                  <h4 className="text-lg font-bold mb-2">How do sponsorships launch?</h4>
                  <p className="text-base md:text-lg leading-relaxed text-textPrimary">
                    After review and approval, Ballotr works with the sponsor to confirm
                    timing, placement details, destination link, and final sponsorship language
                    before launch.
                  </p>
                </div>
              </div>
            </section>
          </section>

          <section className="mt-14 rounded-2xl border border-gray-200 bg-gray-50 p-6 md:p-8 shadow-sm">
            <div className="max-w-3xl">
              <h2 className="text-2xl md:text-3xl font-bold text-textPrimary mb-4">
                Follow Ballotr
              </h2>
              <p className="text-base md:text-lg text-textSecondary leading-relaxed mb-6">
                Stay connected with Ballotr for platform updates, new conversations, and
                ongoing civic engagement across our social channels.
              </p>
              <SocialLinks variant="page" />
            </div>
          </section>

          <div className="mt-12 text-center">
            <a
              href="/auth"
              className="bg-ballotr-blue hover:bg-ballotr-blue-light text-white font-semibold px-6 py-3 rounded-md shadow-md transition"
            >
              Get Started — Create Your Account
            </a>
          </div>
        </div>
      </main>
    </>
  );
};

export default AboutPage;